import { useState, useCallback } from "react";
import { Reservation } from "../types/Reservation";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { isInternalDepartment } from "../../calendar/utils/internalDepartments";

// Función para crear una fecha en UTC
const crearFechaUTC = (fecha: string | Date): Date => {
  if (typeof fecha === "string") {
    return parseISO(fecha);
  }
  return fecha;
};

// Función para verificar si dos reservas se superponen
const reservasSeSuperponen = (
  entrada1: Date,
  salida1: Date,
  entrada2: Date,
  salida2: Date
): boolean => {
  return entrada1 < salida2 && entrada2 < salida1;
};

// Función para verificar si una reserva se superpone con una lista de reservas
export const hayConflicto = (
  reserva: Reservation,
  reservas: Reservation[],
  reservaId?: number
): boolean => {
  return reservas.some((r) => {
    if (r.id === reservaId) return false;
    if (r.departamento !== reserva.departamento) return false;
    if (isInternalDepartment(reserva.departamento)) return false;

    const reservaEntrada = crearFechaUTC(r.entrada);
    const reservaSalida = crearFechaUTC(r.salida);
    const nuevaEntrada = crearFechaUTC(reserva.entrada);
    const nuevaSalida = crearFechaUTC(reserva.salida);

    return reservasSeSuperponen(
      nuevaEntrada,
      nuevaSalida,
      reservaEntrada,
      reservaSalida
    );
  });
};

// Función para obtener detalles de la superposición
const obtenerDetallesSuperposicion = (
  reserva: Reservation,
  reservas: Reservation[],
  reservaId?: number
): { tipoSuperposicion: string; reservas: Reservation[] } => {
  const reservasSuperpuestas = reservas.filter((r) => {
    if (r.id === reservaId) return false;
    if (r.departamento !== reserva.departamento) return false;
    if (isInternalDepartment(reserva.departamento)) return false;

    const reservaEntrada = crearFechaUTC(r.entrada);
    const reservaSalida = crearFechaUTC(r.salida);
    const nuevaEntrada = crearFechaUTC(reserva.entrada);
    const nuevaSalida = crearFechaUTC(reserva.salida);

    return reservasSeSuperponen(
      nuevaEntrada,
      nuevaSalida,
      reservaEntrada,
      reservaSalida
    );
  });

  if (reservasSuperpuestas.length === 0) {
    return {
      tipoSuperposicion: "",
      reservas: [],
    };
  }

  const detalles = reservasSuperpuestas.map((r) => {
    const entrada = format(crearFechaUTC(r.entrada), "dd/MM/yyyy", {
      locale: es,
    });
    const salida = format(crearFechaUTC(r.salida), "dd/MM/yyyy", {
      locale: es,
    });
    return `${r.nombre} ${r.apellido} (${entrada} - ${salida})`;
  });

  return {
    tipoSuperposicion: `El departamento ya está reservado por:\n${detalles.join(
      "\n"
    )}`,
    reservas: reservasSuperpuestas,
  };
};

export const useReservaValidacion = () => {
  const [overlapError, setOverlapError] = useState<string | null>(null);

  const checkOverlap = useCallback(
    (reserva: Reservation, reservas: Reservation[], esEdicion = false) => {
      if (!reserva.entrada || !reserva.salida || !reserva.departamento) {
        setOverlapError(null);
        return false;
      }

      if (isInternalDepartment(reserva.departamento)) {
        setOverlapError(null);
        return false;
      }

      const reservaId = esEdicion ? reserva.id : undefined;
      const haySuperposicion = hayConflicto(reserva, reservas, reservaId);

      if (haySuperposicion) {
        const detalles = obtenerDetallesSuperposicion(
          reserva,
          reservas,
          reservaId
        );
        setOverlapError(detalles.tipoSuperposicion);
        return true;
      }

      setOverlapError(null);
      return false;
    },
    []
  );

  return {
    overlapError,
    checkOverlap,
  };
};
